import { memo } from 'react';

import type { GlassMaterial } from '@typings/glass';

import styles from './glass.module.scss';
import type { LiquidFilterState } from './useLiquidFilter';

interface LiquidFilterProps {
  readonly state: LiquidFilterState;
  readonly material: GlassMaterial;
}

/**
 * The refraction step of a liquid surface's backdrop-filter.
 *
 * Only the displacement lives here. Blur and saturation run as native CSS
 * functions either side of it, in one filter list (see useGlass):
 *
 *   backdrop-filter: blur(..) url(#this-filter) saturate(..)
 *
 * Measured, not assumed: with blur inside the SVG, a page of liquid
 * surfaces dropped about one frame in ten while scrolling. Chromium's
 * native backdrop blur is far cheaper than feGaussianBlur, and it mirrors
 * the backdrop at the box edge instead of fading to transparent, so the
 * rim no longer darkens either.
 *
 *   feImage           loads this surface's displacement map.
 *   feDisplacementMap bends the already-frosted backdrop along the rim.
 *                     `scale` is twice the refraction distance because a
 *                     channel only swings half its range from neutral.
 *
 * `color-interpolation-filters="sRGB"` is load-bearing. The default,
 * linearRGB, converts the map before reading it, and 128 then no longer
 * means "no displacement": the whole backdrop would shift.
 *
 * userSpaceOnUse units let the filter region and the map match the
 * element's box pixel for pixel.
 */
export const LiquidFilter = memo(({ state, material }: LiquidFilterProps) => (
  <svg className={styles.filterHost} aria-hidden="true" focusable="false">
    <filter
      id={state.id}
      x="0"
      y="0"
      width={state.width}
      height={state.height}
      filterUnits="userSpaceOnUse"
      colorInterpolationFilters="sRGB"
    >
      <feImage
        href={state.mapUrl}
        x="0"
        y="0"
        width={state.width}
        height={state.height}
        preserveAspectRatio="none"
        result="map"
      />
      <feDisplacementMap
        in="SourceGraphic"
        in2="map"
        scale={material.refraction * 2}
        xChannelSelector="R"
        yChannelSelector="G"
      />
    </filter>
  </svg>
));

LiquidFilter.displayName = 'LiquidFilter';
